/**
 * Client-side JWT payload helpers (exp only — no signature verification).
 * Used to avoid sending expired Bearer tokens and to schedule proactive refresh.
 */

const JWT_PARTS = 3;

function decodeJwtPayload(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== JWT_PARTS) {
      return null;
    }
    const segment = parts[1];
    if (!segment) {
      return null;
    }
    const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as { exp?: number };
  } catch {
    return null;
  }
}

/** Unix expiry in milliseconds, or null when the token is not a decodable JWT. */
export function getJwtExpiryMs(token: string): number | null {
  const payload = decodeJwtPayload(token);
  if (payload?.exp === undefined || !Number.isFinite(payload.exp)) {
    return null;
  }
  return payload.exp * 1000;
}

/** True when the token is missing, unparseable, or within `bufferMs` of expiry. */
export function isJwtExpiredOrExpiringSoon(
  token: string | null | undefined,
  bufferMs = 0
): boolean {
  if (!token) {
    return true;
  }
  const expiryMs = getJwtExpiryMs(token);
  if (expiryMs === null) {
    return true;
  }
  return Date.now() >= expiryMs - bufferMs;
}

/** Default lead time before access-token expiry to trigger proactive refresh (5 minutes). */
export const ACCESS_TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;
